import { inject, Injectable, Injector, NgZone, type ErrorHandler } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ToastService } from '@core/services/toast.service';
import { TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { filter } from 'rxjs';

const RELOAD_KEY_PREFIX = 'chunk_reload_at_';
const RELOAD_COOLDOWN_MS = 30_000;
const FALLBACK_MESSAGE = 'Loading error. Please reload the page manually.';

// eslint-disable-next-line @angular-eslint/use-injectable-provided-in
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  private readonly zone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly sentryHandler = Sentry.createErrorHandler();
  private readonly chunkPattern = new RegExp(String.raw`${globalThis.location.origin}/chunk-[A-Za-z0-9]+\.js`);
  private readonly handledChunks = new Set<string>();

  constructor() {
    inject(Router).events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.handledChunks.clear();
        Object.keys(sessionStorage)
          .filter((k) => k.startsWith(RELOAD_KEY_PREFIX))
          .forEach((k) => sessionStorage.removeItem(k));
      });
  }

  handleError(error: unknown): void {
    const chunkUrl = this.extractChunkUrl(error);
    if (chunkUrl === null) {
      this.sentryHandler.handleError(error);
      return;
    }

    if (this.handledChunks.has(chunkUrl)) {
      return;
    }
    this.handledChunks.add(chunkUrl);

    const reloadedAt = Number(sessionStorage.getItem(RELOAD_KEY_PREFIX + chunkUrl) ?? 0);
    if (Date.now() - reloadedAt >= RELOAD_COOLDOWN_MS) {
      sessionStorage.setItem(RELOAD_KEY_PREFIX + chunkUrl, String(Date.now()));
      this.zone.run(() => globalThis.location.reload());
    } else {
      const message = this.translateMessage();
      this.zone.run(() => this.injector.get(ToastService).show(message, 'error', 10_000));
    }
  }

  private extractChunkUrl(error: unknown): string | null {
    if (!(error instanceof TypeError)) {
      return null;
    }
    const match = this.chunkPattern.exec(error.message);
    return match ? match[0] : null;
  }

  private translateMessage(): string {
    try {
      const result = this.injector.get(TranslocoService).translate('errors.chunk_load_failed');
      return result !== 'errors.chunk_load_failed' ? result : FALLBACK_MESSAGE;
    } catch {
      return FALLBACK_MESSAGE;
    }
  }
}
